import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import copy from "copy-to-clipboard";
import { orderSummaryStages } from "../../constants/orderSummary";
import { Address } from "../../dto/orderSummaryTypes";
import { StepIcon } from "../../common/orderDetails/OrderStepsBar";
import TrackDetails from "../../common/orderDetails/trackDetails";
import { ActionButton } from "../../common/actionButton/ActionButton";
import Toast from "../../common/Toast";
import colors from "../../theme/Colours";
import {
  customerOrderCardStyle,
  headerRowStyle,
  addressTypographyStyle,
  addressDetailStyle,
  orderIdBoxStyle,
  orderIdLabelStyle,
  orderIdValueStyle,
  copyButtonStyle,
  cardContentStyle,
  stepperStyle,
  stepLabelTypographyStyle,
  downloadButtonContainerStyle,
  helpBoxStyle,
  helpBoxTitleStyle,
  helpBoxSubtitleStyle,
  helpPhoneBoxStyle,
  invoiceInfoCardStyle,
  invoiceInfoIconStyle,
  invoiceInfoTextStyle,
} from "./OrderTrackingStyles";
import Info from "../../assets/productCard/info.svg";

type StatusPoint = { status: string; date?: string; time?: string };

type Props = {
  orderId: string;
  statusHistory: StatusPoint[];
  shippingAddress?: Address;
  billingAddress: Address;
  currentStage: number;
  helpEmail?: string;
  canDownload?: boolean;
  onDownloadInvoice?: () => void;
  isOrderCancelled?: boolean;
  orderStatus?: string;
  paymentStatus?: string;
  onMakePayment?: () => void;
};

export default function CustomerOrderLeft({
  orderId,
  shippingAddress,
  billingAddress,
  currentStage,
  statusHistory,
  helpEmail = "support@maltora.app",
  canDownload = false,
  onDownloadInvoice,
  isOrderCancelled = false,
  orderStatus,
  paymentStatus,
  onMakePayment,
}: Props) {
  const [toastMessage, setToastMessage] = React.useState<string>("");
  const [showToast, setShowToast] = React.useState<boolean>(false);

  const addr = shippingAddress?.address ? shippingAddress : billingAddress;

  const copyId = () => {
    if (isOrderCancelled) return;
    try {
      const success = copy(orderId, { format: "text/plain" });
      if (success) {
        setToastMessage("Copied to Clipboard");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2500);
      }
    } catch (error) {
      console.error("Failed to copy order ID:", error);
    }
  };

  return (
    <>
      <Card sx={customerOrderCardStyle}>
        {/* Header row: address + order id */}
        <Box sx={headerRowStyle}>
          <Box>
            <Typography variant="subtitle1" sx={addressTypographyStyle}>
              Your package will be delivered at
            </Typography>
            <Typography
              variant="body2"
              color={colors.greyTypography}
              sx={addressDetailStyle}
            >
              {addr.address}
            </Typography>
          </Box>

          <Box sx={orderIdBoxStyle}>
            <Box>
              <Typography variant="caption" sx={orderIdLabelStyle}>
                Order ID :
              </Typography>
              <Typography variant="body2" sx={orderIdValueStyle}>
                {orderId}
              </Typography>
            </Box>
            {!isOrderCancelled && (
              <Tooltip title="Copy">
                <IconButton size="small" onClick={copyId} sx={copyButtonStyle}>
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Horizontal stepper */}
        <CardContent sx={cardContentStyle}>
          <Stepper alternativeLabel activeStep={currentStage} sx={stepperStyle}>
            {orderSummaryStages.map((s) => (
              <Step key={s.title}>
                <StepLabel slots={{ stepIcon: StepIcon }}>
                  <Typography
                    variant="caption"
                    sx={stepLabelTypographyStyle(
                      orderSummaryStages.indexOf(s) <= currentStage,
                      orderSummaryStages.indexOf(s) === currentStage
                    )}
                  >
                    {s.title}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {(onMakePayment || (canDownload && !isOrderCancelled)) && (
            <Box sx={downloadButtonContainerStyle}>
              {/* Make Payment Button - always visible; clickable only when payment is PENDING */}
              {onMakePayment && (
                <ActionButton
                  label="Make Payment"
                  onClick={() => onMakePayment?.()}
                  disabled={paymentStatus !== "PENDING"}
                  sx={{
                    width: 180,
                    marginRight: 1,
                    backgroundColor: colors.primaryButtonOrange,
                    color: "white",
                    "&:hover": {
                      backgroundColor: colors.primaryButtonOrangeHover,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: colors.primaryButtonOrange,
                      color: "white",
                      opacity: 0.5,
                    },
                  }}
                />
              )}

              {canDownload && !isOrderCancelled && onDownloadInvoice && (
                <ActionButton
                  label="Download Invoice"
                  onClick={onDownloadInvoice}
                  sx={{ width: 180 }}
                />
              )}
            </Box>
          )}

          {!isOrderCancelled && currentStage <= 2 && (
            <Box sx={invoiceInfoCardStyle}>
              <Box
                component="img"
                src={Info}
                alt="Info"
                sx={invoiceInfoIconStyle}
              />
              <Typography variant="body2" sx={invoiceInfoTextStyle}>
                Note: The invoice will be available for generation once the
                consignment is ready for dispatch
              </Typography>
            </Box>
          )}

          {/* Track details (accordion) */}
          <TrackDetails
            statusHistory={statusHistory}
            currentStage={currentStage}
            disabled={isOrderCancelled}
          />

          {/* Help box */}
          <Box sx={helpBoxStyle}>
            <Box>
              <Typography variant="subtitle2" sx={helpBoxTitleStyle}>
                Have trouble with your package?
              </Typography>
              <Typography variant="body2" sx={helpBoxSubtitleStyle}>
                You can mail us at support@maltora.app.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
      />
    </>
  );
}
